import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Injector } from '@angular/core';
import { AuthService } from './auth.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  constructor(
    private authService: AuthService,
    private router: Router,
    private injector: Injector,
  ) {}

  private readonly companySelectRoute = '/applicationadmin/company-select';
  private readonly applicationSelectRoute =
    '/applicationadmin/application-select';
  private readonly loginRoute = '/auth/login';
  private readonly sessionUserIdentityKey = 'authSessionUserIdentity';
  private readonly applicationAdminSelectionFlag =
    'applicationAdminCompanySelected';
  private readonly selectedCompanyKey = 'applicationAdminSelectedCompanyId';
  private readonly globalSelectedCompanyKey = 'applicationAdminActiveCompanyId';
  private readonly superAdminSelectionFlag = 'superAdminApplicationSelected';
  private readonly selectedApplicationKey = 'superAdminSelectedApplicationId';
  private readonly globalSelectedApplicationKey =
    'superAdminActiveApplicationId';
  private readonly emptyGuid = '00000000-0000-0000-0000-000000000000';
  private readonly exemptRoutes = [
    '/auth/login',
    '/auth/sso-login',
    '/auth/sso-callback',
    '/auth/forgot-password',
    '/auth/forgot-success',
    '/auth/create-password',
    '/auth/confirm-email',
    '/auth/set-password',
    '/auth/change-email',
    '/auth/verify-otp',
    '/auth/logout',
  ];

  private getStorageUser(): any | null {
    const storageUserRaw = localStorage.getItem('currentUser');
    if (!storageUserRaw) {
      return null;
    }

    try {
      return JSON.parse(storageUserRaw);
    } catch {
      return null;
    }
  }

  private getUserIdentity(user: any): string {
    return String(user?.id || user?.userId || user?.email || '');
  }

  private hasCrossTabUserMismatch(currentUser: any): boolean {
    const currentIdentity = this.getUserIdentity(currentUser);
    if (!currentIdentity) {
      return false;
    }

    const storageUser = this.getStorageUser();
    const storageIdentity = this.getUserIdentity(storageUser);
    const storageToken = String(storageUser?.token || '');
    const currentToken = String(currentUser?.token || '');
    const tabIdentity = sessionStorage.getItem(this.sessionUserIdentityKey);

    if (!tabIdentity) {
      sessionStorage.setItem(this.sessionUserIdentityKey, currentIdentity);
      return false;
    }

    if (tabIdentity !== currentIdentity) {
      if (
        storageIdentity === currentIdentity &&
        storageToken === currentToken
      ) {
        sessionStorage.setItem(this.sessionUserIdentityKey, currentIdentity);
        return false;
      }

      return !!storageUser;
    }

    const inMemoryIdentity = currentIdentity;

    if (!storageUser) {
      return true;
    }

    if (!inMemoryIdentity || !storageIdentity) {
      return false;
    }

    return (
      inMemoryIdentity !== storageIdentity ||
      String(currentUser?.token || '') !== String(storageUser?.token || '')
    );
  }

  private hasApplicationAdminCompanyContextMismatch(): boolean {
    const tabSelectedCompanyId = sessionStorage.getItem(
      this.selectedCompanyKey,
    );
    if (!tabSelectedCompanyId) {
      return false;
    }

    const globalCompanyFromKey = localStorage.getItem(
      this.globalSelectedCompanyKey,
    );

    let globalCompanyFromUser: string | null = null;
    const storageUserRaw = localStorage.getItem('currentUser');
    if (storageUserRaw) {
      try {
        const storageUser = JSON.parse(storageUserRaw);
        globalCompanyFromUser =
          storageUser?.companyId || storageUser?.branchId || null;
      } catch {
        globalCompanyFromUser = null;
      }
    }

    const globalCompanyId = globalCompanyFromKey || globalCompanyFromUser;

    if (!globalCompanyId) {
      return false;
    }

    return String(tabSelectedCompanyId) !== String(globalCompanyId);
  }

  /** SuperAdmin: bu tab'in uygulama secimi, global (cross-tab) secimden saptiysa true. */
  private hasSuperAdminApplicationContextMismatch(): boolean {
    const tabSelectedApplicationId = sessionStorage.getItem(
      this.selectedApplicationKey,
    );
    if (!tabSelectedApplicationId) {
      return false;
    }

    const globalApplicationFromKey = localStorage.getItem(
      this.globalSelectedApplicationKey,
    );

    let globalApplicationFromUser: string | null = null;
    const storageUser = this.getStorageUser();
    const storageApplicationId = storageUser?.applicationId;
    if (storageApplicationId && storageApplicationId !== this.emptyGuid) {
      globalApplicationFromUser = storageApplicationId;
    }

    const globalApplicationId =
      globalApplicationFromKey || globalApplicationFromUser;

    if (!globalApplicationId) {
      return false;
    }

    return String(tabSelectedApplicationId) !== String(globalApplicationId);
  }

  private closeOpenOverlays(): void {
    const modalService = this.injector.get(NgbModal);
    if (modalService.hasOpenModals()) {
      modalService.dismissAll();
    }

    if (Swal.isVisible()) {
      Swal.close();
    }
  }

  checkArry(ar1: any[] | undefined, ar2: string | any[] | undefined) {
    if (ar1 === undefined || ar2 === undefined) {
      return false;
    }
    return ar1.some((v) => ar2.includes(v));
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const currentUser = this.authService.currentUserValue;
    const urlWithoutParams = state.url.split('?')[0];
    const isExemptRoute = this.exemptRoutes.some((routePath) =>
      urlWithoutParams.startsWith(routePath),
    );

    if (currentUser) {
      const navigatingToLogin = state.url.startsWith(this.loginRoute);
      // console.log('Current User:', currentUser);
      // console.log('Navigating to Login:', navigatingToLogin);
      // console.log('Is Exempt Route:', isExemptRoute);

      // Refresh-token mekanizması eklenmeden önce oluşturulmuş oturumlar (JWT var
      // ama refreshToken yok) — sayfa hiç HTTP isteği atmadan da guard seviyesinde
      // yakalanıp yeniden login'e yönlendirilir.
      if (
        !navigatingToLogin &&
        !isExemptRoute &&
        currentUser.token &&
        !currentUser.refreshToken
      ) {
        this.closeOpenOverlays();
        this.authService.logout();
        return false;
      }

      if (
        !navigatingToLogin &&
        !isExemptRoute &&
        this.hasCrossTabUserMismatch(currentUser)
      ) {
        this.closeOpenOverlays();
        sessionStorage.removeItem(this.applicationAdminSelectionFlag);
        sessionStorage.removeItem(this.selectedCompanyKey);
        sessionStorage.removeItem(this.superAdminSelectionFlag);
        sessionStorage.removeItem(this.selectedApplicationKey);
        sessionStorage.removeItem(this.sessionUserIdentityKey);
        this.authService.currentUserValue = undefined;
        this.router.navigate([this.loginRoute], {
          queryParams: { reason: 'session-changed' },
        });
        return false;
      }

      const isApplicationAdmin =
        currentUser.roles?.includes('ApplicationAdmin');
      const isSuperAdmin = currentUser.roles?.includes('SuperAdmin');
      const hasCompletedSelection =
        sessionStorage.getItem(this.applicationAdminSelectionFlag) === 'true';
      const navigatingToCompanySelect = state.url.startsWith(
        this.companySelectRoute,
      );

      // SuperAdmin: sirket kapisindan ONCE uygulama secim kapisi.
      const hasCompletedApplicationSelection =
        sessionStorage.getItem(this.superAdminSelectionFlag) === 'true';
      const navigatingToApplicationSelect = state.url.startsWith(
        this.applicationSelectRoute,
      );

      if (isSuperAdmin) {
        // company-select dahil hicbir sayfaya uygulama secilmeden gidilemez
        if (
          !hasCompletedApplicationSelection &&
          !navigatingToApplicationSelect &&
          !isExemptRoute
        ) {
          this.router.navigate([this.applicationSelectRoute]);
          return false;
        }

        if (
          hasCompletedApplicationSelection &&
          !navigatingToApplicationSelect &&
          !isExemptRoute &&
          this.hasSuperAdminApplicationContextMismatch()
        ) {
          sessionStorage.removeItem(this.superAdminSelectionFlag);
          sessionStorage.removeItem(this.applicationAdminSelectionFlag);
          this.router.navigate([this.applicationSelectRoute], {
            queryParams: { reason: 'application-context-changed' },
          });
          return false;
        }

        if (navigatingToApplicationSelect) {
          return true;
        }
      }

      // Sirket kapilari: ApplicationAdmin'in yaninda, uygulamasini secmis
      // SuperAdmin de sirket secimini tamamlamak zorundadir.
      const needsCompanyGate = isApplicationAdmin || isSuperAdmin;
      // SustainabilityTeam: secime ZORLANMAZ ama sirket baglami makinesi
      // (cross-tab mismatch + company-select rota izni) onun icin de gecerlidir.
      const usesCompanyContext =
        needsCompanyGate || currentUser.roles?.includes('SustainabilityTeam');

      if (
        needsCompanyGate &&
        !hasCompletedSelection &&
        !navigatingToCompanySelect &&
        !isExemptRoute
      ) {
        this.router.navigate([this.companySelectRoute]);
        return false;
      }

      if (
        usesCompanyContext &&
        hasCompletedSelection &&
        !navigatingToCompanySelect &&
        !isExemptRoute &&
        this.hasApplicationAdminCompanyContextMismatch()
      ) {
        sessionStorage.removeItem(this.applicationAdminSelectionFlag);
        this.router.navigate([this.companySelectRoute], {
          queryParams: { reason: 'company-context-changed' },
        });
        return false;
      }

      if (usesCompanyContext && navigatingToCompanySelect) {
        return true;
      }

      // check if route is restricted by role
      if (
        route.data.roles &&
        !this.checkArry(route.data.roles, currentUser.roles)
      ) {
        // role not authorised so redirect to not-authorized page
        this.router.navigate(['/error/404']);
        return false;
      }

      // authorised so return true
      return true;
    }

    // not logged in so redirect to login page with the return url
    // this.router.navigate(['/auth/loginjtr'], { queryParams: { returnUrl: state.url } });
    this.closeOpenOverlays();
    this.router.navigate(['/auth/loginjtr']);
    return false;
  }
}
